import { getAuthenticationToken } from "../../auth/helpers";
import { buildConfigForEnvironment } from "../config";
import { readPersistedFlags, writePersistedFlags } from "./persistedFlags";
import { GetFeatureFlagsResponse, FeatureFlagName } from "./types";

// Set VITE_USE_MOCK_FEATURE_FLAGS=true in .env.local to use mock flags locally.
// Gated on DEV because Vite loads .env.local in every mode, including `vite build`.
// Without this, a manual deploy from a workspace that has the var set ships a bundle
// with the flag values frozen in and the AppConfig fetch tree-shaken away entirely.
const USE_MOCK_FLAGS =
  import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_FEATURE_FLAGS === "true";

const getMockFlags = (): Record<string, boolean> => ({
  enableIplClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_IPL_CLAIMS === "true",
  enableWcCanadaClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_WC_CANADA_CLAIMS === "true",
  enableCargoJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_CARGO_JSON_FORMS === "true",
  enableJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_JSON_FORMS === "true",
  enableFormStudio: import.meta.env.VITE_FEATURE_FLAG_ENABLE_FORM_STUDIO === "true",
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// A Lambda cold start behind this endpoint was measured at 16-27 s in beta and
// gamma. Aborting earlier and retrying only restarts the wait while the cold
// instance finishes initialising, so the request is aborted only at the API
// Gateway integration limit (29 s), after which no response can arrive.
const REQUEST_CEILING_MS = 30_000;

// Retries cover failures that come back fast (network blips, 5xx, 429). A
// timeout uses up the ceiling, so it is never retried.
const MAX_FETCH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 150;

/**
 * How long boot and route loaders block on flags before settling for the best
 * value available. The request itself keeps running past this; its result is
 * delivered to subscribers when it lands.
 */
export const FLAGS_WAIT_MS = 8000;

type FlagsSource = "live" | "persisted";

interface CachedFlags {
  flags: Record<string, boolean>;
  timestamp: number;
  source: FlagsSource;
}

/** Carries the HTTP status so retry classification can distinguish 5xx from 4xx. */
class FeatureFlagsHttpError extends Error {
  constructor(readonly status: number) {
    super(`Failed to fetch feature flags: ${status}`);
    this.name = "FeatureFlagsHttpError";
  }
}

/** Thrown by waitForFeatureFlags when neither live nor persisted flags exist by the deadline. */
export class FeatureFlagsTimeoutError extends Error {
  constructor(waitMs: number) {
    super(`Feature flags did not arrive within ${waitMs}ms`);
    this.name = "FeatureFlagsTimeoutError";
  }
}

/** A 4xx other than 429 is a decision, not a blip, so retrying cannot change it. */
const isRetryable = (error: unknown): boolean => {
  if (error instanceof FeatureFlagsHttpError) {
    return error.status === 429 || error.status >= 500;
  }
  // Network failures.
  return true;
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

let cachedFlags: CachedFlags | null = null;
let inFlightRequest: Promise<Record<string, boolean>> | null = null;
let inFlightStartedAt = 0;
const listeners = new Set<() => void>();

// The client config types endpoint loosely; ours is always a URL string.
const getApiEndpoint = (): string => `${buildConfigForEnvironment().endpoint}`;

const setCachedFlags = (entry: CachedFlags | null): void => {
  cachedFlags = entry;
  listeners.forEach((listener) => listener());
};

/**
 * Notifies the listener whenever the in-memory flags change, including when a
 * request that outlived every waiter finally lands. Shaped for useSyncExternalStore.
 */
export const subscribeToFeatureFlags = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Persisted values never count as fresh: a live fetch must still replace them.
const isCacheValid = (): boolean => {
  if (!cachedFlags || cachedFlags.source !== "live") return false;
  return Date.now() - cachedFlags.timestamp < CACHE_TTL_MS;
};

/** Returns the same object until the flags change, so it is safe as a useSyncExternalStore snapshot. */
export const getCachedFlags = (): Record<string, boolean> | null => {
  return cachedFlags?.flags ?? null;
};

/**
 * Returns true once feature flags are held in memory, whether fetched live,
 * from mock flags, or adopted from the last-known-good copy.
 *
 * Use this to distinguish "flags failed / haven't finished loading" from
 * "flag is explicitly false". Without this check, consumers reading flags
 * via getCachedFlags()/FeatureFlags would silently fall back to false
 * during the loading window or after a fetch failure.
 */
export const areFeatureFlagsLoaded = (): boolean => cachedFlags !== null;

/**
 * Falls back to the last-known-good flags when nothing is in memory yet.
 * Returns whatever is in memory, or null when there is no usable fallback.
 */
const adoptPersistedFlags = (): Record<string, boolean> | null => {
  if (cachedFlags) return cachedFlags.flags;
  if (USE_MOCK_FLAGS) return null;

  const persisted = readPersistedFlags(getApiEndpoint());
  if (!persisted) return null;

  setCachedFlags({ flags: persisted, timestamp: Date.now(), source: "persisted" });
  return persisted;
};

/**
 * Resolves with live flags, waiting for the request however long it takes (up
 * to the request ceiling). Most callers want waitForFeatureFlags, which bounds
 * the wait.
 */
// TODO: Replace with Smithy client once GetFeatureFlagsCommand is available
export const fetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (isCacheValid() && cachedFlags) {
    return cachedFlags.flags;
  }

  // Deduplicate concurrent requests
  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightStartedAt = Date.now();
  inFlightRequest = doFetchFeatureFlags();
  try {
    return await inFlightRequest;
  } finally {
    inFlightRequest = null;
  }
};

const fallbackOrThrow = (error: unknown): Record<string, boolean> => {
  const fallback = adoptPersistedFlags();
  if (fallback) return fallback;
  // Re-throw so consumers can distinguish an outage from a disabled feature.
  throw error;
};

const doFetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (USE_MOCK_FLAGS) {
    const flags = getMockFlags();
    setCachedFlags({ flags, timestamp: Date.now(), source: "live" });
    return flags;
  }

  const token = await getAuthenticationToken();
  if (!token) {
    return fallbackOrThrow(new Error("No authentication token available"));
  }

  const apiEndpoint = getApiEndpoint();
  const url = `${apiEndpoint}/getFeatureFlags`;
  const deadline = Date.now() + REQUEST_CEILING_MS;

  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
    try {
      const flags = await attemptFetchFlags(url, token, deadline);
      setCachedFlags({ flags, timestamp: Date.now(), source: "live" });
      writePersistedFlags(apiEndpoint, flags);
      return flags;
    } catch (error) {
      lastError = error;
      console.error("Error fetching feature flags:", error);

      // A stale value beats waiting longer for a fresh one: consumers can only
      // tell "never loaded" apart from "loaded as false", not stale from fresh.
      if (cachedFlags?.flags) {
        return cachedFlags.flags;
      }

      if (!isRetryable(error) || attempt === MAX_FETCH_ATTEMPTS) {
        break;
      }

      const delay = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      if (Date.now() + delay >= deadline) {
        break;
      }
      await sleep(delay);
    }
  }

  return fallbackOrThrow(lastError);
};

const attemptFetchFlags = async (
  url: string,
  token: string,
  deadline: number
): Promise<Record<string, boolean>> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), Math.max(0, deadline - Date.now()));

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new FeatureFlagsHttpError(response.status);
    }

    const data: GetFeatureFlagsResponse = await response.json();
    return data.flags ?? {};
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Resolves with the best flags available within a bounded wait.
 *
 * - Flags already in memory (fresh, stale, or last-known-good) resolve at once;
 *   a refresh runs in the background and reaches subscribers when it lands.
 * - Otherwise this waits for the live request, but only until `waitMs` after
 *   that request started. The deadline is shared, so a loader running after
 *   boot already waited does not add a second full wait.
 * - At the deadline it falls back to the last-known-good flags, and throws
 *   FeatureFlagsTimeoutError when there are none. The request keeps running.
 * - A request that fails before the deadline has already tried the same
 *   fallback, so it rejects with its own error: an outage is reported as
 *   one rather than as a timeout.
 */
export const waitForFeatureFlags = async (
  waitMs: number = FLAGS_WAIT_MS
): Promise<Record<string, boolean>> => {
  const request = fetchFeatureFlags();

  if (cachedFlags) {
    request.catch(() => undefined);
    return cachedFlags.flags;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timeoutId = setTimeout(
      () => resolve(null),
      Math.max(0, inFlightStartedAt + waitMs - Date.now())
    );
  });

  try {
    const flags = await Promise.race([request, deadline]);
    if (flags) return flags;
  } finally {
    clearTimeout(timeoutId);
  }

  // Its outcome reaches consumers through the subscription, not this caller.
  request.catch(() => undefined);

  const fallback = adoptPersistedFlags();
  if (fallback) return fallback;
  throw new FeatureFlagsTimeoutError(waitMs);
};

export const getFeatureFlag = async (
  flagName: FeatureFlagName,
  defaultValue = false
): Promise<boolean> => {
  try {
    const flags = await waitForFeatureFlags();
    return flags[flagName] ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

/** Thrown when a flag value could not be established because the lookup failed. */
export class FeatureFlagsUnavailableError extends Error {
  constructor(flagName: FeatureFlagName) {
    super(`Feature flags unavailable; cannot resolve "${flagName}"`);
    this.name = "FeatureFlagsUnavailableError";
  }
}

/**
 * Resolves a flag, distinguishing "explicitly off" from "lookup failed".
 *
 * getFeatureFlag collapses both into false, which makes a failing flags
 * request indistinguishable from a disabled feature. Callers that turn a
 * false into a user-facing denial need to tell those apart, otherwise an
 * outage is reported to the user as a permissions problem.
 *
 * Bounded by the same deadline as waitForFeatureFlags, so a slow backend
 * yields a 503 within seconds rather than holding the route.
 *
 * @throws {FeatureFlagsUnavailableError} when no flag values could be loaded.
 */
export const requireFeatureFlag = async (flagName: FeatureFlagName): Promise<boolean> => {
  const enabled = await getFeatureFlag(flagName);

  // Stale and last-known-good values count as loaded: the value is known even
  // if the latest refresh has not landed or failed.
  if (!enabled && !areFeatureFlagsLoaded()) {
    throw new FeatureFlagsUnavailableError(flagName);
  }

  return enabled;
};

export const clearFeatureFlagsCache = (): void => {
  inFlightRequest = null;
  setCachedFlags(null);
};
